import type { LeadTodoPriority, LeadTodoStatus } from './types';

export const statusDefinitions = [
  { value: 'NOT_CONTACTED', label: 'Ikke kontaktet' },
  { value: 'CONTACTED_FOLLOW_UP', label: 'Kontaktet – opfølgning' },
  { value: 'NEEDS_ASSESSMENT', label: 'Behov afdækning' },
  { value: 'AWAITING_MATERIAL', label: 'Afventer materiale' },
  { value: 'ANALYSIS_MARKET', label: 'Analyse / marked' },
  { value: 'AGREEMENT_MADE', label: 'Agreement made' },
  { value: 'CUSTOMER_ACTIVE', label: 'Kunde – aktiv' },
  { value: 'CUSTOMER_LOST', label: 'Kunde – tabt' },
  { value: 'CONTACTED_NOT_RELEVANT', label: 'Kontaktet – ikke relevant' }
];

export const statusLabelMap = new Map(statusDefinitions.map((item) => [item.value, item.label]));

export const statusTagColors: Record<string, string> = {
  NOT_CONTACTED: 'blue',
  CONTACTED_FOLLOW_UP: 'geekblue',
  NEEDS_ASSESSMENT: 'orange',
  AWAITING_MATERIAL: 'cyan',
  ANALYSIS_MARKET: 'purple',
  AGREEMENT_MADE: 'gold',
  CUSTOMER_ACTIVE: 'green',
  CUSTOMER_LOST: 'volcano',
  CONTACTED_NOT_RELEVANT: 'magenta'
};

export const statusChartColors: Record<string, string> = {
  NOT_CONTACTED: '#1677ff',
  CONTACTED_FOLLOW_UP: '#2f54eb',
  NEEDS_ASSESSMENT: '#fa8c16',
  AWAITING_MATERIAL: '#13c2c2',
  ANALYSIS_MARKET: '#722ed1',
  AGREEMENT_MADE: '#faad14',
  CUSTOMER_ACTIVE: '#52c41a',
  CUSTOMER_LOST: '#d4380d',
  CONTACTED_NOT_RELEVANT: '#eb2f96'
};

export const leadDefinitionSummary =
  'Et lead er en potentiel kunde, som har vist reel interesse for forsikringsrådgivning og kan kontaktes lovligt og professionelt af en mægler.';

export const leadDefinitionCriteria = [
  'Er identificeret (virksomhedsnavn, kontaktperson eller tilsvarende oplysninger).',
  'Har vist interesse eller er varmt anbefalet af en tredjepart.',
  'Har et forventet behov for forsikringsrådgivning baseret på dialog, samtykke eller valide indikatorer.',
  'Er klar til første kontakt fra en mægler.'
];

export type LeadTier = 'LEAD' | 'IN_PROGRESS' | 'CUSTOMER';

export const leadTierDefinitions: Array<{ value: LeadTier; label: string; description: string }> = [
  {
    value: 'LEAD',
    label: 'Lead',
    description: 'Introduceret eller kvalificeret lead som endnu ikke er i aktivt forløb.'
  },
  {
    value: 'IN_PROGRESS',
    label: 'Sag',
    description: 'Aktivt arbejdsforløb efter kontakt, behovsafdækning, materiale eller analyse.'
  },
  {
    value: 'CUSTOMER',
    label: 'Kunde',
    description: 'Underskrevet aftale eller eksisterende kunde med udbetalt provision.'
  }
];

export const leadTierDefinitionMap = new Map(leadTierDefinitions.map((item) => [item.value, item]));

export const leadTierTagColors: Record<LeadTier, string> = {
  LEAD: 'default',
  IN_PROGRESS: 'gold',
  CUSTOMER: 'green'
};

const leadTierByStatus: Record<string, LeadTier> = {
  NOT_CONTACTED: 'LEAD',
  CONTACTED_NOT_RELEVANT: 'LEAD',
  CONTACTED_FOLLOW_UP: 'IN_PROGRESS',
  NEEDS_ASSESSMENT: 'IN_PROGRESS',
  AWAITING_MATERIAL: 'IN_PROGRESS',
  ANALYSIS_MARKET: 'IN_PROGRESS',
  AGREEMENT_MADE: 'CUSTOMER',
  CUSTOMER_ACTIVE: 'CUSTOMER',
  CUSTOMER_LOST: 'CUSTOMER'
};

export function deriveLeadTier(status: string | null | undefined): LeadTier {
  if (!status) {
    return 'LEAD';
  }
  const normalized = status.trim().toUpperCase();
  return leadTierByStatus[normalized] ?? 'LEAD';
}

export const currencyOptions = [
  { value: 'DKK', label: 'DKK' },
  { value: 'EUR', label: 'EUR' },
  { value: 'USD', label: 'USD' },
  { value: 'SEK', label: 'SEK' },
  { value: 'NOK', label: 'NOK' }
];

export const languageOptions = [
  { value: 'da', label: 'Dansk' },
  { value: 'en', label: 'English' },
  { value: 'sv', label: 'Svensk' },
  { value: 'no', label: 'Norsk' },
  { value: 'de', label: 'Deutsch' }
];

export function formatAmount(amount: unknown, currency?: string) {
  if (amount === undefined || amount === null || amount === '') {
    return '-';
  }

  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return '-';
  }

  const isoCurrency = currency && typeof currency === 'string' ? currency : 'DKK';
  try {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: isoCurrency,
      maximumFractionDigits: 0
    }).format(numeric);
  } catch {
    return `${isoCurrency} ${numeric}`;
  }
}

export function formatCvrNumber(value: string | null | undefined) {
  if (!value) {
    return '';
  }
  const normalized = String(value).trim();
  const digits = normalized.replace(/\D+/g, '');
  return digits || normalized;
}

export function formatDateTimeShort(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return new Intl.DateTimeFormat('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (error) {
    console.error('Failed to format timestamp (short)', error);
    return null;
  }
}

export function formatDateTimeFull(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return new Intl.DateTimeFormat('da-DK', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  } catch (error) {
    console.error('Failed to format timestamp (full)', error);
    return null;
  }
}

export const todoStatusLabels: Record<LeadTodoStatus, string> = {
  NEW: 'Ny',
  IN_PROGRESS: 'I gang',
  HANDLED: 'Håndteret',
  DONE: 'Afsluttet',
  CANCELLED: 'Annulleret'
};

export const todoStatusColors: Record<LeadTodoStatus, string> = {
  NEW: 'default',
  IN_PROGRESS: 'processing',
  HANDLED: 'gold',
  DONE: 'success',
  CANCELLED: 'default'
};

export const todoPriorityLabels: Record<LeadTodoPriority, string> = {
  LOW: 'Lav',
  MEDIUM: 'Middel',
  HIGH: 'Høj'
};

export const todoPriorityColors: Record<LeadTodoPriority, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'volcano'
};
